import React, { useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import { FormattedMessage } from "react-intl";
import { ILastVisits } from "types/Recent";
import testIds from "constants/testIds";
import { useStateMachine } from "little-state-machine";
import { handleSuggestionExpand } from "store/store";
import moment from "moment";
import NavContext from "./NavContext";

interface IRecentProps {
  setSelectedSearch: Function;
  setShowRecent: Function;
}

function Recent({ setSelectedSearch, setShowRecent }: IRecentProps) {
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const navRef = React.useRef<HTMLInputElement>(null);
  const { actions } = useStateMachine({ handleSuggestionExpand });
  const getLastVisits = Cookies.get("lastVisits") && JSON.parse(Cookies.get("lastVisits") || "");
  const lastSearches =
    Cookies.get("lastSearches") &&
    JSON.parse(Cookies.get("lastSearches") || "").filter(
      (item: string, index: number, inputArray: string[]) => inputArray.indexOf(item) === index,
    );

  const lastVisits = getLastVisits.reduce((unique: string[], o: any) => {
    if (!unique.some((obj: any) => obj.reference_number === o.reference_number)) {
      unique.push(o);
    }
    return unique;
  }, []);

  const handleCloseSidebar = () => {
    setNavigationConfig({ ...navigationConfig, expanded: false, isShown: !navigationConfig.isMobile });
    navigationConfig.handleNavigation(navigationConfig.expanded);
    setShowRecent(false);
  };

  const handleSearch = (value: string) => {
    setSelectedSearch(value);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setShowRecent(false);
        actions.handleSuggestionExpand({
          manageSuggestion: true,
        });
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [navRef]);

  const renderUrl = (shipment: any) => {
    if (shipment?.display_type === "Quick request") return `/shipment/${shipment?.reference_number}/quick`;
    if (shipment?.display_type === "Request") return `/shipment/${shipment?.reference_number}`;
    return `${shipment?.path}`;
  };

  return (
    <div className="recent-item" data-testid={testIds.RECENT_MENU} ref={navRef}>
      {lastSearches?.length > 0 && (
        <div data-testid={testIds.RECENT_LAST_SEARCHES}>
          <h5 className="mb-10">
            <FormattedMessage id="recent.search" defaultMessage="Last 5 Searches" />
          </h5>
          <div className="d-flex flex-column">
            {lastSearches.reverse().map((value: string) => (
              <label
                key={value}
                className="font-b3 bg-cobalt-light-hover text-capitalize"
                onClick={() => handleSearch(value)}
              >
                {value}
              </label>
            ))}
          </div>
        </div>
      )}

      {lastVisits?.length > 0 && (
        <div data-testid={testIds.RECENT_LAST_VISITS}>
          <h5 className="mt-24">
            <FormattedMessage id="recent.visit" defaultMessage="Last 5 Visits" />
          </h5>
          <div>
            {lastVisits
              .reverse() // Most recent visit first
              .slice(0, 5) // Only show 5 visits
              .map((visit: ILastVisits) => (
                <Link
                  key={visit.path}
                  className="d-flex flex-column justify-content-center align-items-start br-3 bg-cobalt-light drop-shadow overflow-hidden link-reset text-capitalize"
                  to={renderUrl(visit)}
                  onClick={handleCloseSidebar}
                >
                  <span className="font-b4">{visit?.display_type}</span>
                  <span className="font-b3">
                    {visit?.reference_number} {visit?.title && "-"} {visit?.title}
                  </span>
                  <span className="text-secondary font-b3 mb-1">{moment(visit?.date).fromNow()}</span>
                  {visit?.extra_field_data?.primary_tracking_number && (
                    <span className="mt-1 font-b3">{visit?.extra_field_data?.primary_tracking_number}</span>
                  )}
                  <span className="mt-1 font-b3">{visit?.extra_field_data?.location}</span>
                </Link>
              ))}
          </div>
        </div>
      )}
      {lastSearches.length < 1 && lastVisits.length < 1 && (
        <p className="ml-1 text-secondary">
          <FormattedMessage id="recent.noSearchHistory" defaultMessage="No search history" />
        </p>
      )}
    </div>
  );
}

export default Recent;
